import {
  Bot,
  Briefcase,
  Server,
  Workflow,
  Code2,
  Rocket,
  ShieldCheck,
} from 'lucide-react';

export const services = [
  {
    slug: 'ai-automation',
    name: 'AI Transformation & Automation',
    icon: Bot,
    image: 'undraw_ai-chat_ljb9',
    tagline: 'Turn AI potential into operational reality.',
    desc: 'We help you identify, build and deploy AI that actually moves the needle, from readiness assessment to production.',
    tags: ['AI Agents', 'Document AI', 'GenAI', 'Automation'],
    offerings: [
      { title: 'AI Readiness Assessment', desc: 'Where AI can help today, what data you need, and expected effort and ROI.' },
      { title: 'Intelligent Document Processing', desc: 'Invoices, receipts, contracts and compliance documents processed automatically.' },
      { title: 'Workflow Automation', desc: 'Manual, repetitive work routed, processed and approved without human touches.' },
      { title: 'AI Agents & Copilots', desc: 'Customer support, internal help, financial analysis and compliance monitoring agents.' },
      { title: 'Generative AI Integration', desc: 'LLMs, retrieval-augmented pipelines and guardrails built into your products.' },
      { title: 'Data Strategy & Analytics', desc: 'Clean data foundations and reporting that make AI and decisions trustworthy.' },
    ],
    outcomes: [
      { value: 'Up to 80%', label: 'less manual effort' },
      { value: '3–5x', label: 'faster operations' },
      { value: 'Weeks', label: 'not months to first deployment' },
    ],
    color: '#2563EB',
  },
  {
    slug: 'business-consulting',
    name: 'Business Consulting',
    icon: Briefcase,
    image: 'undraw_neighbors_z879',
    tagline: 'Strategy that turns complexity into clarity.',
    desc: 'Growing businesses hit a wall where spreadsheets and duct-taped processes stop working. We help you build the systems for the next stage.',
    tags: ['Process Audit', 'Strategy', 'Growth', 'Ops'],
    offerings: [
      { title: 'Process Assessment & Audit', desc: 'A structured view of what is working and what is leaking time and money.' },
      { title: 'Digital Transformation Strategy', desc: 'A pragmatic roadmap from where you are to a modern operating model.' },
      { title: 'Operational Efficiency', desc: 'Targeted changes to processes and tooling that reduce cost per outcome.' },
      { title: 'Growth & Scale Advisory', desc: 'Operating systems and team structures that hold up as you grow.' },
      { title: 'Tech Stack Optimization', desc: 'Consolidating overlapping tools and cutting what is not earning its keep.' },
      { title: 'Change Management', desc: 'Adoption and training so new systems actually get used.' },
    ],
    outcomes: [
      { value: '30–50%', label: 'lower operating costs' },
      { value: 'Fewer', label: 'redundant tools & vendors' },
      { value: 'Clear', label: '90-day execution roadmap' },
    ],
    color: '#1D4ED8',
  },
  {
    slug: 'technology-consulting',
    name: 'Technology Consulting',
    icon: Server,
    image: 'undraw_security-on_3ykb',
    tagline: 'Modernize your technology for the next stage of growth.',
    desc: 'Architecture, cloud and platform decisions made by engineers who have shipped at scale, vendor-agnostic and outcome-focused.',
    tags: ['Architecture', 'Cloud', 'Modernization', 'Security'],
    offerings: [
      { title: 'Technology Architecture Review', desc: 'An independent look at your architecture with a prioritized risk and action list.' },
      { title: 'Cloud Strategy & Migration', desc: 'AWS, GCP or Azure, right-sized, cost-conscious and secure.' },
      { title: 'Platform Modernization', desc: 'Refactoring and replatforming decisions that reduce technical debt.' },
      { title: 'Integration Strategy', desc: 'CRM, ERP, accounting, payments and operations working as one system.' },
      { title: 'Security & Compliance Review', desc: 'Practical hardening aligned to SOC 2, ISO 27001 and GDPR expectations.' },
      { title: 'Technology Roadmapping', desc: 'A prioritized plan that maps investment to business outcomes.' },
    ],
    outcomes: [
      { value: 'Lower', label: 'infrastructure costs' },
      { value: 'Faster', label: 'ship cycles & deploys' },
      { value: 'Clear', label: 'risk & compliance posture' },
    ],
    color: '#0EA5E9',
  },
  {
    slug: 'process-automation',
    name: 'Business Process Automation',
    icon: Workflow,
    image: 'undraw_printing-invoices_g6c9',
    tagline: 'Automate the work that is slowing you down.',
    desc: 'Most teams spend 30–40% of operational hours on manual, repetitive tasks. We turn that into automated, reliable workflows.',
    tags: ['Workflows', 'Automation', 'Efficiency', 'ROI'],
    offerings: [
      { title: 'Invoice & Document Processing', desc: 'Capture, validate and route documents with AI and rule engines.' },
      { title: 'Expense Management', desc: 'Submission, policy checks and approvals without chasing paper.' },
      { title: 'Approval Workflows', desc: 'The right approvals, in the right order, with full audit trails.' },
      { title: 'Reconciliation Automation', desc: 'Bank, ledger and platform data matched automatically.' },
      { title: 'Report Generation', desc: 'Automated reporting your team trusts, delivered on schedule.' },
      { title: 'Vendor Management', desc: 'Onboarding, contracts and payments orchestrated end to end.' },
    ],
    outcomes: [
      { value: '80%', label: 'reduced manual effort' },
      { value: '3–5x', label: 'faster operations' },
      { value: '99.9%', label: 'accuracy on automated steps' },
    ],
    color: '#0891B2',
  },
  {
    slug: 'custom-solutions',
    name: 'Custom Solutions',
    icon: Code2,
    image: 'undraw_product-demo_9d4i',
    tagline: 'Software built exactly for how you work.',
    desc: 'Custom applications, integrations and dashboards on modern stacks: clean code, shipped fast, no black boxes.',
    tags: ['Web Apps', 'Integrations', 'Dashboards', 'API'],
    offerings: [
      { title: 'Custom Web Applications', desc: 'React, Node.js and Python, battle-tested modern stacks.' },
      { title: 'ERP & CRM Extensions', desc: 'Fill the gaps your platform of record cannot cover out of the box.' },
      { title: 'Operations Dashboards', desc: 'Real-time visibility your leadership and teams actually use.' },
      { title: 'API Development & Integration', desc: 'Systems that talk to each other without fragile exports.' },
      { title: 'Internal Tools & Portals', desc: 'Small, high-leverage tools that remove daily friction.' },
      { title: 'Data Migration & ETL', desc: 'Clean, verified moves between systems: no data loss, no surprises.' },
    ],
    outcomes: [
      { value: '2–4 wks', label: 'to first working build' },
      { value: '100%', label: 'owned by you' },
      { value: 'Modern', label: 'clean, maintainable code' },
    ],
    color: '#16A34A',
  },
  {
    slug: 'implementation',
    name: 'Implementation Services',
    icon: Rocket,
    image: 'undraw_completed_vjc6',
    tagline: 'From purchase to productivity. We get you live.',
    desc: 'Migration, configuration, integration, training and support. We get systems adopted, not just installed.',
    tags: ['Migration', 'Training', 'Support', 'Integrate'],
    offerings: [
      { title: 'Data Migration', desc: 'Careful, verified migrations with rollback plans.' },
      { title: 'System Setup & Configuration', desc: 'Platforms configured to match how your team actually works.' },
      { title: 'Integration & Middleware', desc: 'Tally, ERPs, CRMs and payment gateways wired together.' },
      { title: 'User Training & Adoption', desc: 'Teams trained and onboarded until the system is the habit.' },
      { title: 'Managed Support', desc: 'Ongoing operational support after go-live.' },
      { title: 'Performance Optimization', desc: 'Tuning systems as your data and usage grow.' },
    ],
    outcomes: [
      { value: '<2 wks', label: 'typical go-live' },
      { value: 'Hands-off', label: 'for your team after handover' },
      { value: 'Adopted', label: 'not just installed' },
    ],
    color: '#7C3AED',
  },
  {
    slug: 'qa-testing',
    name: 'QA & Testing',
    icon: ShieldCheck,
    image: 'undraw_document-warning_2es6',
    tagline: 'Ship with confidence, not bugs.',
    desc: 'Manual QA, automated end-to-end testing and load testing, standalone or embedded in your build cycle.',
    tags: ['Manual QA', 'Playwright', 'Load Testing', 'QA Sprint'],
    offerings: [
      { title: 'Manual QA', desc: 'Thoughtful, exploratory testing with clear bug reports.' },
      { title: 'E2E Automation', desc: 'Playwright test suites that run on every release.' },
      { title: 'Load & Performance Testing', desc: 'k6 and Locust profiles that catch bottlenecks before users do.' },
      { title: 'Dedicated QA Sprints', desc: '1–3 week pre-release certification of your feature set.' },
      { title: 'QA Process Setup', desc: 'Testing strategy, environments and workflows your team can sustain.' },
      { title: 'Regression Testing', desc: 'Automated safety nets so shipping never breaks what worked.' },
    ],
    outcomes: [
      { value: 'Early', label: 'bug detection in cycle' },
      { value: 'Fast', label: 'automated regression' },
      { value: 'Confident', label: 'release cadence' },
    ],
    color: '#16A34A',
  },
];
